<script setup lang="ts">
import axios from 'axios'
import { Check, Clock } from 'lucide-vue-next'

import type { ColumnDef, RowData } from '@/components/ui/Table/interfaces'
import { Checkbox } from '@/ui/checkbox'
import SuspectCustomerFicheDialog from '@/views/customer/fraud/SuspectCustomerFicheDialog.vue'
import {
  FRAUD_RULE_CATEGORIES,
  FRAUD_RULES,
  type FraudRuleHit,
  type FraudRuleSlug,
  type FraudSuspectStatus,
  type SuspectCustomer
} from '@/views/customer/fraud/suspectCustomersMock'

type StatusFilter = 'all' | FraudSuspectStatus

interface SuspectRowData {
  customer: SuspectCustomer
  rules: FraudRuleHit[]
  status: FraudSuspectStatus
  storeName: string | null
}

interface StoreOpeningHoursPayload {
  storeId: number
  name: string
  openingHours: unknown[]
}

const { t, locale } = useI18n()
const notification = useNotification()
const { hasPermissionBySlug } = useAccess()

const customers = ref<SuspectCustomer[]>([])
const selectedRuleSlugs = ref<FraudRuleSlug[]>([...FRAUD_RULES])
const selectedStatus = ref<StatusFilter>('all')
const isLoading = ref(true)
const isAllRulesSelected = computed(
  () =>
    selectedRuleSlugs.value.length === FRAUD_RULES.length &&
    FRAUD_RULES.every((slug) => selectedRuleSlugs.value.includes(slug))
)
const statusOptions = computed<{ value: StatusFilter; label: string }[]>(() => [
  { value: 'all', label: t('pages.customer.fraudSuspects.filters.status.all') },
  { value: 'ALERTE', label: t('pages.customer.fraudSuspects.status.ALERTE') },
  { value: 'SURVEILLANCE', label: t('pages.customer.fraudSuspects.status.SURVEILLANCE') }
])
const storesWithoutOpeningHours = ref<string[]>([])
const canEditOpeningHours = computed(() => hasPermissionBySlug('global-configuration.read'))
const missingOpeningHoursKey = computed(() =>
  storesWithoutOpeningHours.value.length === 1
    ? 'pages.customer.fraudSuspects.missingOpeningHours.description'
    : 'pages.customer.fraudSuspects.missingOpeningHours.descriptionPlural'
)
const missingOpeningHoursStores = computed(() =>
  new Intl.ListFormat(locale.value, { style: 'long', type: 'conjunction' }).format(storesWithoutOpeningHours.value)
)
const openedCustomer = ref<SuspectCustomer | null>(null)
const openedRule = ref<FraudRuleHit | null>(null)
const isEventsOpen = ref(false)

const pageTitle = 'pages.customer.fraudSuspects.title'

const columns = computed<Record<string, ColumnDef>>(() => ({
  customer: {
    id: 'customer',
    label: t('pages.customer.fraudSuspects.columns.customer')
  },
  store: {
    id: 'store',
    label: t('pages.customer.fraudSuspects.columns.store')
  },
  rules: {
    id: 'rules',
    label: t('pages.customer.fraudSuspects.columns.rules')
  },
  status: {
    id: 'status',
    label: t('pages.customer.fraudSuspects.columns.status')
  }
}))

const filteredCustomers = computed(() => {
  const byStatus =
    selectedStatus.value === 'all'
      ? customers.value
      : customers.value.filter((customer) => customer.status === selectedStatus.value)

  if (selectedRuleSlugs.value.length === 0) return []
  if (isAllRulesSelected.value) return byStatus

  return byStatus.filter((customer) => customer.rules.some((rule) => selectedRuleSlugs.value.includes(rule.slug)))
})

const rows = computed<RowData[]>(() =>
  filteredCustomers.value.map((customer) => ({
    id: customer.id,
    data: {
      customer,
      rules: visibleRules(customer),
      status: customer.status,
      storeName: customer.storeName
    }
  }))
)

const visibleRules = (customer: SuspectCustomer) => {
  if (isAllRulesSelected.value) return customer.rules

  return customer.rules.filter((rule) => selectedRuleSlugs.value.includes(rule.slug))
}

const isRuleSelected = (slug: FraudRuleSlug) => selectedRuleSlugs.value.includes(slug)

const showAllRules = () => {
  selectedRuleSlugs.value = [...FRAUD_RULES]
}

const toggleRule = (slug: FraudRuleSlug) => {
  const next = isRuleSelected(slug)
    ? selectedRuleSlugs.value.filter((rule) => rule !== slug)
    : [...selectedRuleSlugs.value, slug]

  selectedRuleSlugs.value = next
}

const formatWindow = (value: string | null) =>
  value === null
    ? ''
    : new Date(value).toLocaleString(locale.value, {
        dateStyle: 'short',
        timeStyle: 'short',
        timeZone: 'Europe/Paris'
      })

const ruleLabel = (slug: FraudRuleSlug) => t(`pages.customer.fraudSuspects.rules.${slug}.label`)

const ruleDescription = (slug: FraudRuleSlug) => t(`pages.customer.fraudSuspects.rules.${slug}.description`)

const openEvents = (customer: SuspectCustomer, rule: FraudRuleHit) => {
  openedCustomer.value = customer
  openedRule.value = rule
  isEventsOpen.value = true
}

const loadStoresWithoutOpeningHours = async () => {
  try {
    const { data } = await axios.get<{ data: StoreOpeningHoursPayload[] }>('/opening-hours')

    storesWithoutOpeningHours.value = data.data
      .filter((store) => store.openingHours.length === 0)
      .map((store) => store.name)
      .sort((left, right) => left.localeCompare(right, locale.value))
  } catch {
    storesWithoutOpeningHours.value = []
  }
}

const loadSuspectCustomers = async () => {
  isLoading.value = true

  try {
    const [{ data }] = await Promise.all([
      axios.get<{ data: SuspectCustomer[] }>('customers/suspects'),
      loadStoresWithoutOpeningHours()
    ])
    customers.value = data.data
  } catch {
    customers.value = []
    storesWithoutOpeningHours.value = []
    notification.create('error', {
      text: t('pages.customer.fraudSuspects.loadError')
    })
  } finally {
    isLoading.value = false
  }
}

onMounted(loadSuspectCustomers)

const asRow = (data: Record<string, unknown>) => data as unknown as SuspectRowData
</script>

<template>
  <div>
    <TheDetailHeader
      :title="t(pageTitle)"
      :parent="{ label: t('pages.customer.customerPage.title'), to: { name: 'CustomerList' } }"
    />

    <div class="space-y-4 px-6 pb-12 sm:px-12">
      <p class="mb-4 text-sm text-foreground-secondary">
        {{ t('pages.customer.fraudSuspects.description') }}
      </p>

      <Alert v-if="storesWithoutOpeningHours.length > 0">
        <Clock />
        <AlertTitle>{{ t('pages.customer.fraudSuspects.missingOpeningHours.title') }}</AlertTitle>
        <AlertDescription class="flex flex-col gap-2">
          <i18n-t :keypath="missingOpeningHoursKey" tag="span">
            <template #stores>
              <span class="font-medium text-foreground">{{ missingOpeningHoursStores }}</span>
            </template>
          </i18n-t>
          <RouterLink
            v-if="canEditOpeningHours"
            :to="{ name: 'StoreList', query: { tab: 'hours' } }"
            class="w-fit font-medium text-foreground underline underline-offset-4"
          >
            {{ t('pages.customer.fraudSuspects.missingOpeningHours.action') }}
          </RouterLink>
        </AlertDescription>
      </Alert>

      <div v-if="customers.length > 0" class="space-y-4 rounded-xl border border-border bg-muted/40 p-4 sm:p-5">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p class="text-sm font-semibold text-foreground">
            {{ t('pages.customer.fraudSuspects.filters.label') }}
          </p>
          <Button
            class="w-full shrink-0 sm:w-auto"
            :variant="isAllRulesSelected ? 'default' : 'outline'"
            @click="showAllRules"
          >
            <Check v-if="isAllRulesSelected" class="size-4" />
            {{ t('pages.customer.fraudSuspects.filters.all') }}
          </Button>
        </div>

        <div v-for="category in FRAUD_RULE_CATEGORIES" :key="category.id" class="space-y-2">
          <h2 class="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {{ t(`pages.customer.fraudSuspects.categories.${category.id}`) }}
          </h2>
          <div class="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            <button
              v-for="slug in category.slugs"
              :key="slug"
              type="button"
              class="flex cursor-pointer gap-3 rounded-lg border-2 bg-background px-3 py-3 text-left transition-colors hover:border-primary/60 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              :class="isRuleSelected(slug) ? 'border-primary bg-primary/5 shadow-sm' : 'border-border'"
              :aria-pressed="isRuleSelected(slug)"
              @click="toggleRule(slug)"
            >
              <Checkbox
                :model-value="isRuleSelected(slug)"
                class="mt-0.5 pointer-events-none"
                tabindex="-1"
                aria-hidden="true"
              />
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-medium text-foreground">{{ ruleLabel(slug) }}</span>
                <span class="mt-1 block text-xs leading-5 text-muted-foreground">
                  <i18n-t
                    v-if="slug === 'outside_opening_hours_before'"
                    keypath="pages.customer.fraudSuspects.rules.outside_opening_hours_before.descriptionWithLink"
                    tag="span"
                  >
                    <template #link>
                      <RouterLink
                        :to="{ name: 'StoreList', query: { tab: 'hours' } }"
                        class="font-medium text-foreground underline underline-offset-4"
                        @click.stop
                      >
                        {{ t('pages.customer.fraudSuspects.rules.outside_opening_hours_before.descriptionLink') }}
                      </RouterLink>
                    </template>
                  </i18n-t>
                  <template v-else>{{ ruleDescription(slug) }}</template>
                </span>
              </span>
            </button>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
        <span class="text-sm text-muted-foreground">{{ t('pages.customer.fraudSuspects.filters.status.label') }}</span>
        <Select v-model="selectedStatus">
          <SelectTrigger class="h-10 w-full sm:w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="option in statusOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <AppTable :columns="columns" :rows="rows" :sort="null" :is-loading="isLoading">
        <template #empty-state>
          <BaseIcon
            :name="customers.length === 0 ? 'HeroCheckCircleIconSolid' : 'HeroMagnifyingGlassIconOutline'"
            class="h-8 w-8"
            :class="customers.length === 0 ? 'text-green-500' : 'text-muted-foreground'"
          />
          <p class="mt-2 text-sm font-medium text-foreground-secondary">
            {{
              customers.length === 0
                ? t('pages.customer.fraudSuspects.emptyState.title')
                : t('pages.customer.fraudSuspects.empty')
            }}
          </p>
        </template>

        <template #td-customer="{ data }">
          <RouterLink
            class="text-primary hover:text-primary/80"
            :to="{ name: 'CustomerDetail', params: { customerId: asRow(data).customer.id } }"
            target="_blank"
            rel="noopener noreferrer"
          >
            <RowCustomerContent
              :id="asRow(data).customer.id"
              :name="asRow(data).customer.name"
              :phone="asRow(data).customer.phone"
            />
          </RouterLink>
        </template>

        <template #td-store="{ data }">
          <span class="text-sm text-foreground">
            {{ asRow(data).storeName ?? t('pages.customer.fraudSuspects.columns.severalStores') }}
          </span>
        </template>

        <template #td-rules="{ data }">
          <ul class="space-y-2">
            <li v-for="(rule, ruleIndex) in asRow(data).rules" :key="`${rule.slug}-${ruleIndex}`">
              <Button
                variant="link"
                size="sm"
                class="h-auto px-0 font-medium"
                @click="openEvents(asRow(data).customer, rule)"
              >
                {{ ruleLabel(rule.slug) }}
              </Button>
              <p class="text-xs text-muted-foreground">
                {{ t('pages.customer.fraudSuspects.detail.events', { count: rule.eventCount }) }}
                <template v-if="rule.pattern">
                  · {{ t('pages.customer.fraudSuspects.detail.pattern', { pattern: rule.pattern }) }}
                </template>
                · {{ formatWindow(rule.windowStartedAt) }} – {{ formatWindow(rule.windowEndedAt) }}
              </p>
            </li>
          </ul>
        </template>

        <template #td-status="{ data }">
          <Badge :variant="asRow(data).status === 'ALERTE' ? 'destructive' : 'secondary'">
            {{ t(`pages.customer.fraudSuspects.status.${asRow(data).status}`) }}
          </Badge>
        </template>
      </AppTable>
    </div>

    <SuspectCustomerFicheDialog v-model:open="isEventsOpen" :customer="openedCustomer" :rule="openedRule" />
  </div>
</template>
